import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';

import { useAuthStore } from '@/state/store/auth';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Circle,
  FileText,
  KeyRound,
  Plus,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getAdminDashboardData } from './actions';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  actionHref: string;
  actionLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { userId, platformRole, user } = useAuthStore();
  const { t } = useTranslation();

  if (!userId) {
    navigate('/login', { replace: true });
  }

  const role = platformRole;
  if (!role || role !== 'sys_admin') {
    if (role === 'sys_editor') {
      navigate(`/admin/templates`, { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => getAdminDashboardData(),
  });

  const hasTemplates = data ? data.templateCount > 0 : false;
  const hasIntegrationKeys = data ? data.hasIntegrationKeys : false;

  const checklistItems: ChecklistItem[] = [
    {
      id: 'integration-keys',
      title: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_INTEGRATIONKEYS_TITLE'),
      description: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_INTEGRATIONKEYS_DESCRIPTION'),
      completed: hasIntegrationKeys,
      actionHref: `/admin/settings`,
      actionLabel: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_INTEGRATIONKEYS_ACTION'),
      icon: KeyRound,
    },
    {
      id: 'create-template',
      title: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_CREATETEMPLATE_TITLE'),
      description: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_CREATETEMPLATE_DESCRIPTION'),
      completed: hasTemplates,
      actionHref: `/admin/templates/new`,
      actionLabel: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_CREATETEMPLATE_ACTION'),
      icon: FileText,
    },
    ...(role === 'sys_admin'
      ? [
          {
            id: 'manage-users',
            title: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_MANAGEUSERS_TITLE'),
            description: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_MANAGEUSERS_DESCRIPTION'),
            completed: false,
            actionHref: `/admin/users`,
            actionLabel: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_MANAGEUSERS_ACTION'),
            icon: Users,
          } as ChecklistItem,
        ]
      : []),
    {
      id: 'review-analytics',
      title: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_REVIEWANALYTICS_TITLE'),
      description: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_REVIEWANALYTICS_DESCRIPTION'),
      completed: false,
      actionHref: `/admin/analytics`,
      actionLabel: t('ADMIN_DASHBOARD_CHECKLIST_ITEMS_REVIEWANALYTICS_ACTION'),
      icon: BarChart3,
    },
  ];

  const completedCount = checklistItems.filter((item) => item.completed).length;
  const progressPercentage = Math.round((completedCount / checklistItems.length) * 100);

  const displayName = user?.firstName || user?.email?.split('@')[0] || '';

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Welcome Header */}
      <div className="space-y-1.5 md:space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-selise-blue" />
          <span className="text-xs md:text-sm font-medium text-globe-grey">
            {t('ADMIN_DASHBOARD_HEADER_BADGE')}
          </span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--fg))] font-heading">
          {t('ADMIN_DASHBOARD_HEADER_WELCOME', { name: displayName })}
        </h1>
        <p className="text-sm md:text-base text-globe-grey">
          {t('ADMIN_DASHBOARD_HEADER_DESCRIPTION')}
        </p>
      </div>

      <div className="grid gap-4 md:gap-6 lg:grid-cols-3">
        {/* Getting Started Checklist */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle>{t('ADMIN_DASHBOARD_CHECKLIST_TITLE')}</CardTitle>
                  <CardDescription>{t('ADMIN_DASHBOARD_CHECKLIST_DESCRIPTION')}</CardDescription>
                </div>
                <div className="text-right shrink-0">
                  {isLoading ? (
                    <Skeleton className="h-8 w-12 ml-auto" />
                  ) : (
                    <>
                      <div className="text-xl md:text-2xl font-bold text-selise-blue font-heading leading-none">
                        {progressPercentage}%
                      </div>
                      <div className="text-[10px] md:text-xs text-globe-grey mt-1">
                        {t('ADMIN_DASHBOARD_CHECKLIST_PROGRESS', {
                          completed: completedCount,
                          total: checklistItems.length,
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full rounded-lg" />
                  ))
                : checklistItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-start gap-3 md:gap-4 rounded-lg border p-3 md:p-4 transition-colors ${
                          item.completed
                            ? 'border-[hsl(var(--lime-green))]/30 bg-[hsl(var(--lime-green))]/5'
                            : 'border-border bg-card'
                        }`}
                      >
                        <div
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                            item.completed
                              ? 'bg-lime-green text-white'
                              : 'border-2 border-border bg-transparent'
                          }`}
                        >
                          {item.completed ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : (
                            <Circle className="h-4 w-4 text-globe-grey" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Stacked column on mobile so the action button gets
                              its own row beneath the description. On md+ the
                              button returns to the right of the text. */}
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Icon
                                  className={`h-4 w-4 shrink-0 ${
                                    item.completed ? 'text-lime-green' : 'text-globe-grey'
                                  }`}
                                />
                                <h3
                                  className={`font-medium text-sm md:text-base truncate ${
                                    item.completed
                                      ? 'text-globe-grey line-through'
                                      : 'text-[hsl(var(--fg))]'
                                  }`}
                                >
                                  {item.title}
                                </h3>
                              </div>
                              <p className="text-xs md:text-sm text-globe-grey">
                                {item.description}
                              </p>
                            </div>
                            {!item.completed && (
                              <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="self-start md:self-auto shrink-0"
                              >
                                <Link
                                  to={item.actionHref}
                                  className="inline-flex items-center gap-1.5"
                                >
                                  {item.actionLabel}
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions — hidden on mobile where the actions live in the
            AdminTopbar dropdown menu instead. */}
        <div className="hidden lg:block space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('ADMIN_DASHBOARD_QUICKACTIONS_TITLE')}</CardTitle>
              <CardDescription>{t('ADMIN_DASHBOARD_QUICKACTIONS_DESCRIPTION')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button asChild className="w-full justify-start" size="lg">
                <Link to={`/admin/templates/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('ADMIN_DASHBOARD_QUICKACTIONS_ADDTEMPLATE')}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start" size="lg">
                <Link to={`/admin/settings`}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t('ADMIN_DASHBOARD_QUICKACTIONS_OPENSETTINGS')}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start" size="lg">
                <Link to={`/admin/analytics`}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  {t('ADMIN_DASHBOARD_QUICKACTIONS_VIEWANALYTICS')}
                </Link>
              </Button>
              {role && role === 'sys_admin' && (
                <Button asChild variant="outline" className="w-full justify-start" size="lg">
                  <Link to={`/admin/users`}>
                    <Users className="mr-2 h-4 w-4" />
                    {t('ADMIN_DASHBOARD_QUICKACTIONS_MANAGEUSERS')}
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
